import { Image, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import axios from '../../../../lib/axios'

export default function ImageBlock({ settings, onInlineUpdate }) {
  const { padding, margin, backgroundColor, borderWidth, borderColor, borderRadius } = settings
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const containerStyle = {
    padding: `${padding.top} ${padding.right} ${padding.bottom} ${padding.left}`,
    margin: `${margin.top} ${margin.right} ${margin.bottom} ${margin.left}`,
    backgroundColor: backgroundColor || 'transparent',
    textAlign: settings.textAlign
  }

  const imageStyle = {
    display: 'block',
    width: settings.width,
    height: settings.height,
    margin: settings.textAlign === 'center' ? '0 auto' : settings.textAlign === 'right' ? '0 0 0 auto' : '0',
    border: (borderWidth && borderWidth !== '0px') ? `${borderWidth} solid ${borderColor || '#cccccc'}` : 'none',
    borderRadius: borderRadius || '0px'
  }

  const handleUploadClick = () => {
    if (onInlineUpdate) {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !onInlineUpdate) return

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post('master-templates/upload-file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (response.data?.url) {
        onInlineUpdate({ src: response.data.url, alt: file.name })
      }
    } catch (err) {
      console.error('Upload failed:', err)
      // Fallback: use local object URL for preview
      const localUrl = URL.createObjectURL(file)
      onInlineUpdate({ src: localUrl, alt: file.name })
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={handleFileChange}
      style={{ display: 'none' }}
    />
  )

  // Show grey placeholder if no src
  if (!settings.src) {
    return (
      <div style={containerStyle}>
        {fileInput}
        <div
          onClick={handleUploadClick}
          style={{
            width: settings.width || '100%',
            maxWidth: '100%',
            height: '180px',
            backgroundColor: '#f1f5f9',
            border: '2px dashed #cbd5e1',
            borderRadius: borderRadius || '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            margin: settings.textAlign === 'center' ? '0 auto' : settings.textAlign === 'right' ? '0 0 0 auto' : '0',
            cursor: onInlineUpdate ? 'pointer' : 'default',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            if (onInlineUpdate) {
              e.currentTarget.style.borderColor = 'var(--color-primary)'
              e.currentTarget.style.backgroundColor = '#eef2ff'
            }
          }}
          onMouseOut={(e) => {
            if (onInlineUpdate) {
              e.currentTarget.style.borderColor = '#cbd5e1'
              e.currentTarget.style.backgroundColor = '#f1f5f9'
            }
          }}
        >
          {uploading ? (
            <>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #e2e8f0',
                borderTopColor: 'var(--color-primary)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontFamily: 'Arial, sans-serif' }}>
                Uploading...
              </span>
            </>
          ) : (
            <>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(99,102,241,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Upload size={24} style={{ color: '#6366f1' }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: '500', fontFamily: 'Arial, sans-serif', display: 'block' }}>
                  {onInlineUpdate ? 'Click to upload image' : 'Upload or add image URL'}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'Arial, sans-serif' }}>
                  JPG, PNG, GIF up to 5MB
                </span>
              </div>
            </>
          )}
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  const imgElement = (
    <img
      src={settings.src}
      alt={settings.alt}
      style={imageStyle}
      onError={(e) => {
        e.target.style.display = 'none'
        e.target.nextSibling.style.display = 'flex'
      }}
    />
  )

  const fallback = (
    <div style={{
      display: 'none',
      width: settings.width || '100%',
      maxWidth: '100%',
      height: '180px',
      backgroundColor: '#f1f5f9',
      border: '1px dashed #cbd5e1',
      borderRadius: borderRadius || '4px',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      margin: settings.textAlign === 'center' ? '0 auto' : settings.textAlign === 'right' ? '0 0 0 auto' : '0'
    }}>
      <Image size={32} style={{ color: '#94a3b8' }} />
      <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontFamily: 'Arial, sans-serif' }}>
        Image failed to load
      </span>
    </div>
  )

  return (
    <div style={containerStyle}>
      {fileInput}
      {settings.link ? (
        <a href={settings.link} target="_blank" rel="noopener noreferrer">
          {imgElement}
          {fallback}
        </a>
      ) : (
        <>
          {imgElement}
          {fallback}
        </>
      )}
    </div>
  )
}
